import React, { useState } from 'react';
import {message, Upload, Modal, Button, Image} from 'antd';
import { DeleteOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';

type PropsType = {
	action?: any;
	value?: any;
	onChange?: any;
	fileSize?: number;
	fileNum?: any;
	setIsUploading?: any;
	disabled?: any;
};

const SPUploadPdfAndImg: React.FC<PropsType> = props => {
	const { onChange, value, fileSize, action, fileNum, setIsUploading, disabled } = props;

	const initFileList = (val: any) => {
		if (val)
			return val.map((f: any) => {
				const url = typeof f === 'string' ? f : f.file_url;
				return {
					uid: url,
					name: url,
					url,
					status: 'done',
					response: {
						data: f,
					},
				};
			});
		else return [];
	};

	const [myFileList, setMyFileList] = useState<UploadFile[]>(initFileList(value));
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewImage, setPreviewImage] = useState<string | undefined>(undefined);
	const [hoveredFileUid, setHoveredFileUid] = useState<string | null>(null);

	const beforeUpload = (file: RcFile) => {
		const isImageOrPDF = file.type === 'application/pdf' || file.type.startsWith('image/');
		if (!isImageOrPDF) {
			message.error('仅支持图片或PDF格式!');
			return Upload.LIST_IGNORE;
		}

		const isLt2M = file.size / 1024 / 1024 < (fileSize || 5);
		if (!isLt2M && file.type.startsWith('image/')) {
			message.error(`文件需小于${fileSize || 5}MB!`);
			return Upload.LIST_IGNORE;
		}

		if (myFileList && myFileList.length >= (fileNum || 1)) {
			message.error(`仅支持上传${fileNum || 1}个文件!`);
			return Upload.LIST_IGNORE;
		}
		return true;
	};

	const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
		setMyFileList(newFileList);
		setIsUploading && setIsUploading(true);

		if (newFileList.every(f => f.status === 'done')) {
			setIsUploading && setIsUploading(false);
			let errFile = newFileList.find(f => f?.response?.data == null);
			if (errFile) message.error(errFile?.response?.msg || errFile?.response?.errmsg);

			let list = newFileList.filter(f => f?.response?.data != null);
			setMyFileList(list);

			onChange?.(list.map(f => f.response.data));
		}
		if (newFileList.find((f: any) => f.status != 'error') == undefined) {
			setIsUploading && setIsUploading && setIsUploading(false);
		}
	};

	const getBase64 = (file: RcFile): Promise<string> =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = error => reject(error);
		});

	const handlePreview = async (file: UploadFile) => {
		if (file.type === 'application/pdf') {
			const fileURL = file.response?.data?.file_url || file.response?.data || '';
			if (fileURL && typeof fileURL === 'string') {
				window.open(fileURL, '_blank');
				return;
			}
		}
		if (file?.url?.toLowerCase?.()?.includes('.pdf')) {
			window.open(file.url, '_blank');
			return;
		}

		if (!file.url && !file.preview) {
			file.preview = await getBase64(file.originFileObj as RcFile);
		}
		setPreviewImage(file.url || file.preview);
		setPreviewVisible(true);
	};

	const handleRemove = (file: UploadFile) => {
		const newFileList = myFileList.filter(f => f.uid !== file.uid);
		setMyFileList(newFileList);
		onChange?.(newFileList.map(f => f.response.data));
	};

	const handleMouseEnter = (uid: string) => {
		setHoveredFileUid(uid);
	};

	const handleMouseLeave = () => {
		setHoveredFileUid(null);
	};

	return (
		<>
			<Upload
				name="file"
				listType="picture-card"
				beforeUpload={beforeUpload}
				action={action}
				onChange={handleChange}
				onPreview={handlePreview}
				fileList={myFileList}
				showUploadList={{
					showPreviewIcon: false, // 禁用默认预览按钮
					showRemoveIcon: false,
				}}
				itemRender={(originNode, file) => (
					<div
						style={{
							position: 'relative',
							width: '100%',
							height: '100%',
						}}
						onMouseEnter={() => handleMouseEnter(file.uid)}
						onMouseLeave={handleMouseLeave}
					>
						{originNode}
						{hoveredFileUid === file.uid && (
							<div
								style={{
									position: 'absolute',
									top: '50%',
									left: '50%',
									transform: 'translate(-50%, -50%)',
									width: '85%',
									height: '85%',
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									background: 'rgba(0, 0, 0, 0.5)',
									zIndex: 1,
								}}
							>
								<Button
									disabled={false}
									icon={<EyeOutlined />}
									style={{ color: 'white' }}
									type="link"
									onClick={() => handlePreview(file)}
								/>
								{disabled ? null : (
									<Button
										icon={<DeleteOutlined />}
										style={{ color: 'white' }}
										type="link"
										onClick={() => handleRemove(file)}
									/>
								)}
							</div>
						)}
					</div>
				)}
			>
				{myFileList.length >= (fileNum || 1) ? null : (
					<div>
						<PlusOutlined />
						<div style={{ marginTop: 8 }}>上传</div>
					</div>
				)}
			</Upload>
      {
        previewVisible && <Image
              preview={{
                visible: previewVisible,
                onVisibleChange: (visible) => setPreviewVisible(visible),
              }}
              wrapperStyle={{ display: 'none' }}
              alt="example" src={previewImage} />
      }
		</>
	);
};

export default SPUploadPdfAndImg;
